/**
 * Fünf Zustände des Statusbands (A6). Keine Ampel, kein Reifegrad.
 */

export type Testzustand =
  | "bereit"
  | "laeuft"
  | "wartet_auf_dich"
  | "erfolgreich"
  | "fehlgeschlagen";

export type StatusbandSchrittPunkt = {
  nr: number;
  code: string | null;
  titel: string;
  /** erledigt = gefüllt, aktuell = Ring, gesperrt = leer + gedämpft */
  marke: "erledigt" | "aktuell" | "gesperrt";
  klickbar: boolean;
};

export type StatusbandAktion = {
  id: string;
  label: string;
  /** Genau eine Aktion darf primär (gefüllt) sein. */
  primaer?: boolean;
  disabled?: boolean;
  onClick: () => void;
};

/** Anzeigetext je Zustand — Klartext, kein Fachjargon. */
export const TESTZUSTAND_TEXT: Record<Testzustand, string> = {
  bereit: "Bereit — starte, wann du willst",
  laeuft: "Läuft …",
  wartet_auf_dich: "Wir warten auf dich",
  erfolgreich: "Erfolgreich",
  fehlgeschlagen: "Fehlgeschlagen",
};

export function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export function formatDauerSekunden(ms: number): string {
  const sec = ms / 1000;
  if (sec < 10) {
    return `${sec.toFixed(1).replace(".", ",")} s`;
  }
  return `${Math.round(sec)} s`;
}

export function formatKostenUsd(usd: number): string {
  const rounded = Math.round(usd * 100) / 100;
  return `${rounded.toFixed(2).replace(".", ",")} $`;
}

/**
 * Handlungsaufforderung für `wartet_auf_dich`.
 * Nutzt Rezeptsignale, erfindet keine produktspezifischen Sätze.
 */
export function handlungsaufforderungAusRezept(args: {
  fertigWenn: Array<{ typ: string; wert?: string }>;
  erlaubteAktionen: string[];
  schrittTitel: string;
}): string {
  if (args.fertigWenn.some((f) => f.typ === "manuell")) {
    return "Wenn du fertig bist, melde es hier.";
  }
  const hatSubmit = args.erlaubteAktionen.some(
    (a) => a.endsWith(".submit") || a.includes("register") || a.includes("speichern"),
  );
  if (hatSubmit) {
    return "Trage die Angaben in der Ansicht ein und sende das Formular ab.";
  }
  if (args.erlaubteAktionen.length > 0) {
    return `Führe „${args.schrittTitel}" in der Ansicht aus — wir warten auf dich.`;
  }
  return `Führe „${args.schrittTitel}" in der Ansicht aus — wir warten auf dich.`;
}

export function bauSchrittkette(args: {
  schrittNummer: number;
  schrittGesamt: number;
  schrittTitel: string;
  bekannte?: Array<{ nr: number; code: string; titel: string }>;
}): StatusbandSchrittPunkt[] {
  const gesamt = Math.max(0, Math.floor(args.schrittGesamt));
  const aktuell = Math.max(1, Math.floor(args.schrittNummer));
  const byNr = new Map(
    (args.bekannte ?? []).map((s) => [s.nr, s] as const),
  );
  const punkte: StatusbandSchrittPunkt[] = [];
  for (let nr = 1; nr <= gesamt; nr += 1) {
    const bekannt = byNr.get(nr);
    const titel =
      nr === aktuell
        ? args.schrittTitel || bekannt?.titel || `Schritt ${nr}`
        : bekannt?.titel || `Schritt ${nr}`;
    const marke: StatusbandSchrittPunkt["marke"] =
      nr < aktuell ? "erledigt" : nr === aktuell ? "aktuell" : "gesperrt";
    const klickbar = marke !== "gesperrt" && Boolean(bekannt?.code);
    punkte.push({
      nr,
      code: bekannt?.code ?? null,
      titel,
      marke,
      klickbar,
    });
  }
  return punkte;
}
